import React, { useMemo } from "react";
import { GridValidRowModel } from "@mui/x-data-grid";
import { Box, Grid } from "@mui/material";
import {
  AttachMoney,
  TrendingUp,
  TrendingDown,
  HomeWork,
  BarChart,
  PeopleAlt,
  Equalizer,
  Business,
  AccountBalanceWallet,
  MoneyOff,
  ShowChart,
} from "@mui/icons-material";

import { GetDealsMetricsQuery } from "../../generated/graphql-operations";
import {
  getTotalAchievedRent,
  getTotalBudgetRent,
  getBaseRentValuation,
  getTotalCurrentRent,
  getAchievedTotalCapital,
  getBudgetTotalCapital,
} from "../../utils/DealUtils";
import { formatter } from "../../utils/CommonFunctions";
import { MetricCard } from "./MetricsSummaryCard";

const formatPercent = (value: number) => {
  if (!value) return "";
  const percentFormat = new Intl.NumberFormat("en-au", {
    style: "percent",
    maximumFractionDigits: 2,
  });
  return percentFormat.format(value);
};

export interface DealsMetricsSummaryProps {
  filteredDeals: GetDealsMetricsQuery["Deals"] | GridValidRowModel[];
}

const DealsMetricsSummary = ({ filteredDeals }: DealsMetricsSummaryProps) => {
  const dealSummary = useMemo(() => {
    const summary = {
      totalAchievedRent: 0,
      totalBudgetRent: 0,
      totalAchievedRentVariance: 0,
      baseRentValuation: 0,
      achievedRentValuationVariance: 0,
      totalCurrentRent: 0,
      achievedToCurrentRentVariance: 0,
      totalAchievedCapital: 0,
      totalBudgetCapital: 0,
      achievedToBudgetCapitalVariance: 0,
    };

    if (!filteredDeals) return summary;

    for (const row of filteredDeals) {
      const dealData = row.DealData?.[0];
      if (dealData) {
        summary.totalAchievedRent += getTotalAchievedRent(dealData);
        summary.totalBudgetRent += getTotalBudgetRent(dealData);
        summary.baseRentValuation += getBaseRentValuation(dealData);
        summary.totalCurrentRent += getTotalCurrentRent(dealData);
        summary.totalAchievedCapital += getAchievedTotalCapital(dealData);
        summary.totalBudgetCapital += getBudgetTotalCapital(dealData);
      }
    }

    // Calculate variances using total numbers with base value as denominator
    if (summary.totalBudgetRent !== 0) {
      summary.totalAchievedRentVariance =
        (summary.totalAchievedRent - summary.totalBudgetRent) /
        summary.totalBudgetRent;
    }
    if (summary.baseRentValuation !== 0) {
      summary.achievedRentValuationVariance =
        (summary.totalAchievedRent - summary.baseRentValuation) /
        summary.baseRentValuation;
    }
    if (summary.totalCurrentRent !== 0) {
      summary.achievedToCurrentRentVariance =
        (summary.totalAchievedRent - summary.totalCurrentRent) /
        summary.totalCurrentRent;
    }
    if (summary.totalBudgetCapital !== 0) {
      summary.achievedToBudgetCapitalVariance =
        (summary.totalAchievedCapital - summary.totalBudgetCapital) /
        summary.totalBudgetCapital;
    }

    return summary;
  }, [filteredDeals]);

  const metricItems = [
    {
      label: "Achieved Gross Rent",
      value: formatter(dealSummary.totalAchievedRent, "$"),
      icon: <AttachMoney fontSize="small" />,
    },
    {
      label: "Achieved Gross Rent",
      value: formatter(dealSummary.totalAchievedRent, "$"),
      icon: <AttachMoney fontSize="small" />,
    },
    {
      label: "Achieved Gross Rent",
      value: formatter(dealSummary.totalAchievedRent, "$"),
      icon: <AttachMoney fontSize="small" />,
    },
    {
      label: "Achieved Capital",
      value: formatter(dealSummary.totalAchievedCapital, "$"),
      icon: <AccountBalanceWallet fontSize="small" />,
    },
    {
      label: "Budget Gross Rent",
      value: formatter(dealSummary.totalBudgetRent, "$"),
      icon: <Business fontSize="small" />,
    },
    {
      label: "Valuation Gross Rent",
      value: formatter(dealSummary.baseRentValuation, "$"),
      icon: <BarChart fontSize="small" />,
    },
    {
      label: "Current / Previous Gross Rent",
      value: formatter(dealSummary.totalCurrentRent, "$"),
      icon: <Equalizer fontSize="small" />,
    },

    {
      label: "Budget Capital",
      value: formatter(dealSummary.totalBudgetCapital, "$"),
      icon: <MoneyOff fontSize="small" />,
    },
    {
      label: "Variance Achieved to Budget Rent",
      value: formatPercent(dealSummary.totalAchievedRentVariance),
      icon: <TrendingUp fontSize="small" />,
    },
    {
      label: "Variance Achieved to Valuation Rent",
      value: formatPercent(dealSummary.achievedRentValuationVariance),
      icon: <ShowChart fontSize="small" />,
    },
    {
      label: "Variance Achieved to Current Rent",
      value: formatPercent(dealSummary.achievedToCurrentRentVariance),
      icon: <TrendingDown fontSize="small" />,
    },
    {
      label: "Variance Achieved to Budget Capital",
      value: formatPercent(dealSummary.achievedToBudgetCapitalVariance),
      icon: <HomeWork fontSize="small" />,
    },
  ];

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="flex-start" mb={3}>
        <Box sx={{ width: { xs: "100%", sm: "50%", md: "25%" } }}>
          <MetricCard
            label="Deal Count"
            value={filteredDeals.length}
            icon={<PeopleAlt fontSize="small" />}
          />
        </Box>
      </Box>
      <Grid container spacing={2}>
        {metricItems.map(({ label, value, icon }) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <MetricCard label={label} value={value} icon={icon} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DealsMetricsSummary;
